/*
How do you create a 7×3 grid (7 days × 3 meals)?
What should each cell contain initially?
How do you get the days of the week for the current week?
*/

import React from 'react';
import { Grid, Typography, Box } from '@mui/material';
import MealCell from "./MealCell";
import usePlanStore from '../../store/planStore';

const WeeklyPlanGrid = ({ plan }) => {
  // plan maps to currentPlan from store
  const daysOfWeek = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const mealTimes = ['Breakfast', 'Lunch', 'Dinner'];
  
  // Get week range from store or plan
  const { currentPlan } = usePlanStore();
  
  const getDishesForMeal = (day, mealTime) => {
    if (!plan || !plan.plans) return [];

    try {
      // Get the actual date for this day
      const dayDate = weekDates[day];
      const dateString = dayDate.toISOString().split('T')[0]; // Format as YYYY-MM-DD
    
      const mealKey = mealTime.toLowerCase();
    
      // Find the day plan by matching the date instead of day_of_week
      const dayPlan = plan.plans.find(e => {
        if (e.date) {
          // If the plan has a date field, compare dates
          const planDate = new Date(e.date).toISOString().split('T')[0];
          return planDate === dateString;
        } else {
          // Fallback to day_of_week matching if no date field
          const dayMap = {
            'Mon': 'Monday',
            'Tue': 'Tuesday', 
            'Wed': 'Wednesday',
            'Thu': 'Thursday',
            'Fri': 'Friday',
            'Sat': 'Saturday',
            'Sun': 'Sunday'
          };
          const fullDayName = dayMap[day];
          return e.day_of_week === fullDayName;
        }
      });
    
      if (dayPlan && dayPlan[mealKey]) {
        return dayPlan[mealKey];
      }
      return [];
    } catch (error) {
      console.error('Error getting dishes for meal:', error);
      return [];
    }
  };

  // Function to get dates for the week
  const getWeekDates = () => {
    const dates = {};
    
    // Try to get start date from current plan or use a default method
    let startDate;
    
    if (currentPlan && currentPlan.start_date) {
      startDate = new Date(currentPlan.start_date);
    } else if (plan && plan.start_date) {
      startDate = new Date(plan.start_date);
    } else {
      // Fallback: use current week
      const today = new Date();
      const dayOfWeek = today.getDay(); // 0 = Sunday, 1 = Monday, etc.
      const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek; // Calculate Monday
      startDate = new Date(today);
      startDate.setDate(today.getDate() + mondayOffset);
    }

    // Generate dates for each day of the week
    daysOfWeek.forEach((day, index) => {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + index);
      dates[day] = date;
    });

    return dates;
  };

  const weekDates = getWeekDates();

  // Function to format date
  const formatDate = (date) => {
    return date.toISOString().split('T')[0];
  };

  return (
    <Box sx={{ p: 2 }}>
      {/* Header Row */}
      <Grid container spacing={1}>
        <Grid item xs={12} sm={3}>
          {/* Empty corner cell */}
        </Grid>
        {mealTimes.map((meal) => (
          <Grid item xs={12} sm={3} key={meal}>
            <Typography variant="h6">{meal}</Typography>
          </Grid>
        ))}
      </Grid>

      {/* Day Rows */}
      {daysOfWeek.map((day) => (
        <Grid container spacing={1} mb={1} key={day}>
          <Grid item xs={12} sm={3}>
            <Box>
              <Typography variant="h6">{day}</Typography>
              <Typography variant="body2" color="text.secondary">
                {formatDate(weekDates[day])}
              </Typography>
            </Box>
          </Grid>
          {mealTimes.map((meal) => (
            <Grid item xs={12} sm={3} key={`${day}-${meal}`}>
              <MealCell
                day={day}
                date={formatDate(weekDates[day])}
                mealTime={meal}
                dishes={getDishesForMeal(day, meal)}
              />
            </Grid>
          ))}
        </Grid>
      ))}
    </Box>
  );
};

export default WeeklyPlanGrid;